import React, { useMemo, useState } from "react";
import {
  Pane,
  Button,
  majorScale,
  toaster,
  SavedIcon,
  Label,
  InlineAlert,
  minorScale,
  Heading,
  Text,
  Strong,
} from "evergreen-ui";
import { isAppVersionOne, User, isTalent } from "../api/users";
import { useForm } from "react-hook-form";
import { useUsers } from "../context/userContext";
import { useCategories } from "../context/categoryContext";

type Props = {
  user: User;
};

type formShape = {
  categories: string[];
};

// only for Talent
const MAX_CATEGORIES = 3;

const UserCategoryForm = ({ user }: Props) => {
  const { updateUserProfile } = useUsers();
  const { oldCategories: oldCategoryList, categories: categoryLists } = useCategories();
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    formState: { errors },
  } = useForm<formShape>({
    defaultValues: { categories: user.profile.categories },
  });

  const handleFormSubmit = async (data: formShape) => {
    setSaving(true);

    const { categories } = data;

    try {
      await updateUserProfile(
        {
          ...user,
          profile: { ...user.profile, categories },
        },
        ["categories"],
      );

      toaster.success("Updated User successfully!");
    } catch (err: any) {
      toaster.danger("Error saving: " + err.message);
    }

    setSaving(false);
  };

  const atLeastOne = () => (getValues("categories").length ? true : "You must select at least one.");

  let errMessage: string = "";
  if (errors.categories) {
    // @ts-ignore
    errMessage = errors.categories.message;
  }

  const categoriesFromWatch = watch("categories");
  const categories = useMemo(() => categoriesFromWatch || [], [categoriesFromWatch]);
  const disabledMaxCheckboxes = useMemo(
    () => isTalent(user) && categories.length >= MAX_CATEGORIES,
    [user, categories],
  );
  const disabledMaxSave = useMemo(() => isTalent(user) && categories.length > MAX_CATEGORIES, [user, categories]);

  return (
    <>
      <Pane marginBottom={majorScale(1)}>
        <Text>
          <Strong>Selected{isTalent(user) ? ` (max of ${MAX_CATEGORIES})` : ""}: </Strong>
          {categories.join(", ")}
        </Text>
      </Pane>
      <Pane is="form" onSubmit={handleSubmit(handleFormSubmit)}>
        <Pane display="flex" flexDirection="column">
          <Pane display="flex" flexDirection="column" height={400} overflowY="scroll">
            {isAppVersionOne(user) ? (
              Object.keys(categoryLists).map((header) => {
                const categoryList = categoryLists[header as keyof typeof categoryLists];

                return (
                  <Pane key={header} flexGrow={1} marginBottom={majorScale(1)} marginTop={majorScale(1)}>
                    <Heading is="h4" size={400} marginBottom={minorScale(1)} style={{ fontWeight: "bold" }}>
                      {header}
                    </Heading>
                    <Pane marginLeft={majorScale(1)}>
                      {categoryList.map((c) => {
                        const fid = "checkbox-" + c.replace(/\W/g, "").toLowerCase();
                        return (
                          <Pane
                            key={c}
                            float="left"
                            display="flex"
                            alignItems="center"
                            marginLeft={minorScale(2)}
                            marginRight={minorScale(2)}
                            marginTop={minorScale(1)}
                            marginBottom={minorScale(1)}
                          >
                            <input
                              id={fid}
                              type="checkbox"
                              value={c}
                              {...register("categories", { validate: atLeastOne })}
                              disabled={!categories.includes(c) && disabledMaxCheckboxes}
                            />
                            <Label htmlFor={fid}>{c}</Label>
                          </Pane>
                        );
                      })}
                    </Pane>
                  </Pane>
                );
              })
            ) : (
              <Pane>
                {oldCategoryList.map((c) => {
                  const fid = "checkbox-" + c.replace(/\W/g, "").toLowerCase();
                  return (
                    <Pane key={c} display="flex" alignItems="center" marginBottom={minorScale(1)}>
                      <input
                        id={fid}
                        type="checkbox"
                        value={c}
                        {...register("categories", { validate: atLeastOne })}
                        disabled={!categories.includes(c) && disabledMaxCheckboxes}
                      />
                      <Label htmlFor={fid}>{c}</Label>
                    </Pane>
                  );
                })}
              </Pane>
            )}
          </Pane>
          {!!errMessage && (
            <InlineAlert intent="danger" marginBottom={majorScale(2)} marginTop={majorScale(2)}>
              {errMessage}
            </InlineAlert>
          )}
          <Pane display="flex" justifyContent="flex-end" marginTop={majorScale(2)}>
            <Button
              appearance="primary"
              iconBefore={SavedIcon}
              type="submit"
              isLoading={saving}
              disabled={disabledMaxSave}
            >
              Save
            </Button>
          </Pane>
        </Pane>
      </Pane>
    </>
  );
};

export default UserCategoryForm;
